export const CHART_V2_RANGE_HORIZONS = [
  'OneDay',
  'OneWeek',
  'TwoWeeks',
  'OneMonth',
  'TwoMonths',
  'ThreeMonths',
  'SixMonths',
  'OneYear',
  'ThreeYears',
  'FiveYears',
  'TenYears',
  'Max',
] as const;

export const CHART_V2_INTERVALS = [
  '1m',
  '5m',
  '15m',
  '30m',
  '1h',
  '1D',
  '1W',
  '1M',
] as const;

export const CHART_V2_INPUT_SERIES_TYPES = [
  'line',
  'bars',
  'candlestick',
] as const;

export const CHART_V2_Y_AXIS_TYPES = ['split', 'merged'] as const;
export const CHART_V2_PAYLOAD_VERSION = 1;

export type ChartV2RangeHorizon = (typeof CHART_V2_RANGE_HORIZONS)[number];
export type ChartV2IntervalValue = (typeof CHART_V2_INTERVALS)[number];
export type ChartV2InputSeriesType =
  (typeof CHART_V2_INPUT_SERIES_TYPES)[number];
export type ChartV2YAxisType = (typeof CHART_V2_Y_AXIS_TYPES)[number];

export type ChartV2CompactHorizon =
  | '1D'
  | '1W'
  | '2W'
  | '1M'
  | '2M'
  | '3M'
  | '6M'
  | '1Y'
  | '3Y'
  | '5Y'
  | '10Y'
  | 'MAX';

export type ChartV2RenderableSeriesType = 'line' | 'bar' | 'candlestick';
export type ChartV2PriceDisplayValue = 'price' | 'indexed';
export type ChartV2DomainValue = string | number;
export interface ChartV2ViewDomainInput {
  start: ChartV2DomainValue;
  end: ChartV2DomainValue;
}
export type ChartV2DomainInput =
  | readonly [ChartV2DomainValue, ChartV2DomainValue]
  | ChartV2ViewDomainInput;

export interface ChartV2OhlcPointInput {
  time: string | number;
  open: number;
  high: number;
  low: number;
  close: number;
}

export interface ChartV2ValuePointInput {
  time: string | number;
  value: number;
}

export type ChartV2PointInput = ChartV2OhlcPointInput | ChartV2ValuePointInput;

export interface ChartV2SeriesIdInput {
  tag: string;
  snake?: string | null;
}

export interface ChartV2AssetReference {
  tag: string;
  snake: string;
  paneIndex?: number;
}

export interface ChartV2PaneSettingsInput {
  paneIndex: number;
  seriesType?: ChartV2InputSeriesType;
  yAxisType?: ChartV2YAxisType;
}

export interface ChartV2ViewSettings {
  resample?: ChartV2IntervalValue;
  horizon?: ChartV2RangeHorizon;
  domain?: ChartV2ViewDomainInput;
  primarySnake?: string;
}

export interface ChartV2Payload {
  version: typeof CHART_V2_PAYLOAD_VERSION;
  assets: ChartV2AssetReference[];
  view?: ChartV2ViewSettings;
  panes?: ChartV2PaneSettingsInput[];
  hiddenSnakes?: string[];
}

export type NormalizedChartV2PaneSettings = Required<ChartV2PaneSettingsInput>;

export const DEFAULT_CHART_V2_PANE_INDEX = 0;
export const DEFAULT_CHART_V2_INPUT_SERIES_TYPE = 'line';
export const DEFAULT_CHART_V2_Y_AXIS_TYPE = 'split';

const RANGE_TO_COMPACT_HORIZON = {
  OneDay: '1D',
  OneWeek: '1W',
  TwoWeeks: '2W',
  OneMonth: '1M',
  TwoMonths: '2M',
  ThreeMonths: '3M',
  SixMonths: '6M',
  OneYear: '1Y',
  ThreeYears: '3Y',
  FiveYears: '5Y',
  TenYears: '10Y',
  Max: 'MAX',
} satisfies Record<ChartV2RangeHorizon, ChartV2CompactHorizon>;

const COMPACT_TO_RANGE_HORIZON = Object.fromEntries(
  Object.entries(RANGE_TO_COMPACT_HORIZON).map(
    ([rangeHorizon, compactHorizon]) => [compactHorizon, rangeHorizon]
  )
) as Record<ChartV2CompactHorizon, ChartV2RangeHorizon>;

export const toChartV2CompactHorizon = (
  horizon: string | null | undefined
): ChartV2CompactHorizon | undefined =>
  horizon && horizon in RANGE_TO_COMPACT_HORIZON
    ? RANGE_TO_COMPACT_HORIZON[horizon as ChartV2RangeHorizon]
    : undefined;

export const toChartV2RangeHorizon = (
  horizon: ChartV2CompactHorizon
): ChartV2RangeHorizon => COMPACT_TO_RANGE_HORIZON[horizon];

export const toChartV2Interval = (
  resample: string | null | undefined
): ChartV2IntervalValue | undefined =>
  resample && CHART_V2_INTERVALS.includes(resample as ChartV2IntervalValue)
    ? (resample as ChartV2IntervalValue)
    : undefined;

export const toChartV2Resample = (
  interval: ChartV2IntervalValue
): ChartV2IntervalValue => interval;

export const toChartV2RenderableSeriesType = (
  seriesType: string | null | undefined
): ChartV2RenderableSeriesType =>
  seriesType === 'bars'
    ? 'bar'
    : seriesType === 'candlestick'
      ? 'candlestick'
      : 'line';

export const toChartV2PriceDisplay = (
  yAxisType: string | null | undefined
): ChartV2PriceDisplayValue => (yAxisType === 'merged' ? 'indexed' : 'price');

export const getChartV2YAxisLabel = ({
  displayFormat,
  snakeCurrency,
  entityCurrency,
}: {
  displayFormat?: string | null;
  snakeCurrency?: string | null;
  entityCurrency?: string | null;
}): string => {
  if (displayFormat === 'perc') {
    return '%';
  }

  const currency = (snakeCurrency || entityCurrency || '').toUpperCase();

  return displayFormat === 'currency' && currency ? currency : '';
};

export const isChartV2FallbackOhlcPoint = (
  point: ChartV2PointInput
): point is ChartV2OhlcPointInput =>
  'close' in point && point.open === 0 && point.high === 0 && point.low === 0;

export const normalizeChartV2FallbackOhlcPoint = <
  TPoint extends ChartV2PointInput,
>(
  point: TPoint,
  seriesType: string | null | undefined = 'line'
): TPoint | ChartV2OhlcPointInput | ChartV2ValuePointInput => {
  if (!isChartV2FallbackOhlcPoint(point)) {
    return point;
  }

  if (seriesType === 'line' || seriesType === 'area') {
    return { time: point.time, value: point.close };
  }

  return {
    time: point.time,
    open: point.close,
    high: point.close,
    low: point.close,
    close: point.close,
  };
};

export const normalizeChartV2FallbackOhlcPoints = <
  TPoint extends ChartV2PointInput,
>(
  points: TPoint[],
  seriesType: string | null | undefined = 'line'
): Array<TPoint | ChartV2OhlcPointInput | ChartV2ValuePointInput> =>
  points.map(point => normalizeChartV2FallbackOhlcPoint(point, seriesType));

export const getChartV2PaneId = (paneIndex: number): string =>
  `pane-${paneIndex}`;

export const getChartV2SeriesId = ({
  tag,
  snake,
}: ChartV2SeriesIdInput): string => `${tag}:${snake ?? 'default'}`;

export const normalizeChartV2AssetReference = ({
  paneIndex = DEFAULT_CHART_V2_PANE_INDEX,
  ...asset
}: ChartV2AssetReference): Required<ChartV2AssetReference> => ({
  ...asset,
  paneIndex,
});

export const normalizeChartV2PaneSettings = ({
  seriesType = DEFAULT_CHART_V2_INPUT_SERIES_TYPE,
  yAxisType = DEFAULT_CHART_V2_Y_AXIS_TYPE,
  ...pane
}: ChartV2PaneSettingsInput): NormalizedChartV2PaneSettings => ({
  ...pane,
  seriesType,
  yAxisType,
});

export const getChartV2PaneSettingsByIndex = (
  panes: ChartV2PaneSettingsInput[] | undefined
): Map<number, NormalizedChartV2PaneSettings> =>
  new Map(
    (panes ?? []).map(pane => [
      pane.paneIndex,
      normalizeChartV2PaneSettings(pane),
    ])
  );

export const getChartV2DefaultPriceDisplayByPane = (
  panes: Pick<ChartV2PaneSettingsInput, 'paneIndex' | 'yAxisType'>[]
): Record<string, ChartV2PriceDisplayValue> =>
  panes.reduce<Record<string, ChartV2PriceDisplayValue>>((result, pane) => {
    const display = toChartV2PriceDisplay(pane.yAxisType);

    if (display === 'indexed') {
      result[getChartV2PaneId(pane.paneIndex)] = display;
    }

    return result;
  }, {});

const resolveDomainValue = <TPoint>({
  value,
  points,
  getTime,
  treatNumberAsIndex,
}: {
  value: ChartV2DomainValue;
  points: TPoint[];
  getTime: (point: TPoint) => string | number;
  treatNumberAsIndex: boolean;
}): number | null => {
  const rawTime =
    typeof value === 'number' && treatNumberAsIndex
      ? points[Math.trunc(value)] && getTime(points[Math.trunc(value)])
      : value;

  if (rawTime === undefined) {
    return null;
  }

  const time = typeof rawTime === 'number' ? rawTime : Date.parse(rawTime);

  return Number.isNaN(time) ? null : time;
};

export const resolveChartV2Domain = <TPoint>({
  domain,
  points,
  getTime,
}: {
  domain: ChartV2DomainInput | null | undefined;
  points: TPoint[];
  getTime: (point: TPoint) => string | number;
}): { start: number; end: number } | null => {
  if (!domain) {
    return null;
  }

  const treatNumberAsIndex = !('start' in domain);
  const [startValue, endValue] = treatNumberAsIndex
    ? domain
    : [domain.start, domain.end];
  const start = resolveDomainValue({
    value: startValue,
    points,
    getTime,
    treatNumberAsIndex,
  });
  const end = resolveDomainValue({
    value: endValue,
    points,
    getTime,
    treatNumberAsIndex,
  });

  if (start === null || end === null || start >= end) {
    return null;
  }

  return { start, end };
};
